import { useCallback, useMemo, useState } from 'react'

import type { FetchCampaignsQueryParams } from '@/ducks/campaigns/api/campaigns.api'
import {
  buildFetchCampaignsParams,
  clearCampaignDateFilters,
  clearCampaignStatusFilters,
  clearCampaignTypeFilters,
  hasActiveCampaignFilters,
  setCampaignCustomDateFilter,
  setCampaignDatePresetFilter
} from '@/ducks/campaigns/campaigns.filters.helpers'
import type {
  CampaignDateRangePreset,
  CampaignFilters,
  CampaignFilterStatus,
  CampaignLimit,
  CampaignType
} from '@/ducks/campaigns/campaigns.types'
import { EMPTY_CAMPAIGN_FILTERS } from '@/ducks/campaigns/campaigns.types'

type UseCampaignsFiltersArgs = {
  page: number
  limit: CampaignLimit
  onPageReset: () => void
}

export type CampaignFilterHandlers = {
  setCampaignTypes: (campaignTypes: CampaignType[]) => void
  setCampaignStatuses: (statuses: CampaignFilterStatus[]) => void
  setDatePreset: (dateRange: CampaignDateRangePreset) => void
  setCustomDateRange: (dateFrom: string, dateTo: string) => void
  clearTypes: () => void
  clearStatuses: () => void
  clearDate: () => void
}

export const useCampaignsFilters = ({
  page,
  limit,
  onPageReset
}: UseCampaignsFiltersArgs) => {
  const [filters, setFilters] = useState<CampaignFilters>(
    EMPTY_CAMPAIGN_FILTERS
  )

  const applyFilters = useCallback(
    (updater: (current: CampaignFilters) => CampaignFilters) => {
      setFilters(current => updater(current))
      onPageReset()
    },
    [onPageReset]
  )

  const handlers = useMemo<CampaignFilterHandlers>(
    () => ({
      setCampaignTypes: campaignTypes =>
        applyFilters(current => ({ ...current, campaignTypes })),
      setCampaignStatuses: statuses =>
        applyFilters(current => ({ ...current, statuses })),
      setDatePreset: dateRange =>
        applyFilters(current =>
          setCampaignDatePresetFilter(current, dateRange)
        ),
      setCustomDateRange: (dateFrom, dateTo) =>
        applyFilters(current =>
          setCampaignCustomDateFilter(current, dateFrom, dateTo)
        ),
      clearTypes: () => applyFilters(clearCampaignTypeFilters),
      clearStatuses: () => applyFilters(clearCampaignStatusFilters),
      clearDate: () => applyFilters(clearCampaignDateFilters)
    }),
    [applyFilters]
  )

  const queryParams = useMemo<FetchCampaignsQueryParams>(
    () => buildFetchCampaignsParams({ page, limit, filters }),
    [filters, limit, page]
  )

  const hasActiveFilters = useMemo(
    () => hasActiveCampaignFilters(filters),
    [filters]
  )

  return {
    filters,
    queryParams,
    hasActiveFilters,
    handlers
  }
}
